/**
 * Shared display helpers for invoice/bill/credit note line items in approval detail panels.
 */
import type { ChangeDetails } from "../../../../shared/utils/reviewWorkflow";

const toFiniteNumber = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const roundToTwoDecimals = (n: number): number =>
  Math.round((n + Number.EPSILON) * 100) / 100;

const isNoTaxLineItem = (item: {
  tax_id?: string | null;
}): boolean => {
  const taxId = String(item?.tax_id ?? "");
  return taxId === "NO_TAX" || taxId === "no_tax";
};

export const getLineItemDisplayTotal = (item: {
  item_total?: number | null;
  rate?: number | null;
  quantity?: number | null;
  amount?: number | null;
}): number => {
  const itemTotal = toFiniteNumber(item?.item_total);
  if (itemTotal !== null) return itemTotal;

  const rate = toFiniteNumber(item?.rate);
  const quantity = toFiniteNumber(item?.quantity);
  if (rate !== null && quantity !== null) return rate * quantity;

  return toFiniteNumber(item?.amount) ?? 0;
};

/**
 * Tax amount for display: tax_amount → inclusive-base diff → rate×qty×tax_percentage.
 * Matches BillsReviewPage / InvoiceReviewPage validation recalculation.
 */
export function getComputedLineItemTaxAmount(item: {
  tax_id?: string | null;
  tax_amount?: number | null;
  item_total_inclusive_of_tax?: number | null;
  item_total?: number | null;
  rate?: number | null;
  quantity?: number | null;
  amount?: number | null;
  tax_percentage?: number | string | null;
}): number {
  if (isNoTaxLineItem(item)) return 0;

  const explicitTax = toFiniteNumber(item?.tax_amount);
  if (explicitTax !== null) return explicitTax;

  const baseTotal = getLineItemDisplayTotal(item);
  const inclusive = toFiniteNumber(item?.item_total_inclusive_of_tax);
  if (inclusive !== null) {
    return roundToTwoDecimals(Math.max(inclusive - baseTotal, 0));
  }

  const pct = toFiniteNumber(item?.tax_percentage);
  if (pct !== null && pct > 0 && baseTotal > 0) {
    return roundToTwoDecimals((baseTotal * pct) / 100);
  }

  return 0;
}

export function getValidationSnapshotLineItems(
  changeDetails: ChangeDetails | null | undefined,
): any[] | null {
  const lines = changeDetails?.validation_snapshot?.line_items;
  return Array.isArray(lines) && lines.length > 0 ? lines : null;
}

export function buildInvoiceBillBeforeRequestedEditsJson(
  changeDetails: ChangeDetails | null | undefined,
  fallbackJson: any,
): any {
  const validationLineItems = getValidationSnapshotLineItems(changeDetails);
  if (!validationLineItems) return fallbackJson;

  const base = changeDetails?.original_json || fallbackJson || {};
  const subTotal = validationLineItems.reduce((sum, item) => {
    return sum + getLineItemDisplayTotal(item);
  }, 0);
  const taxTotal = validationLineItems.reduce((sum, item) => {
    return sum + getComputedLineItemTaxAmount(item);
  }, 0);
  const discount = toFiniteNumber(base?.discount) ?? 0;

  return {
    ...base,
    line_items: validationLineItems,
    sub_total: subTotal,
    tax_total: taxTotal,
    total: subTotal + taxTotal + discount,
  };
}

export function getLineItemInclusiveTotal(item: {
  item_total_inclusive_of_tax?: number | null;
  item_total?: number | null;
  amount?: number | null;
  rate?: number | null;
  quantity?: number | null;
  tax_id?: string | null;
  tax_amount?: number | null;
  tax_percentage?: number | string | null;
}): number {
  const inclusive = toFiniteNumber(item?.item_total_inclusive_of_tax);
  if (inclusive !== null) return inclusive;

  const baseTotal = getLineItemDisplayTotal(item);
  const taxAmount = getComputedLineItemTaxAmount(item);
  return roundToTwoDecimals(baseTotal + taxAmount);
}

export function getLineItemTaxAmount(item: {
  tax_id?: string | null;
  tax_amount?: number | null;
  item_total_inclusive_of_tax?: number | null;
  item_total?: number | null;
  rate?: number | null;
  quantity?: number | null;
  amount?: number | null;
  tax_percentage?: number | string | null;
}): number {
  return getComputedLineItemTaxAmount(item);
}

/**
 * Whether to show the secondary tax row (hide when no tax and no tax name).
 */
export function shouldShowLineItemTaxRow(item: {
  tax_amount?: number | null;
  tax_name?: string | null;
  tax_id?: string | null;
  tax_percentage?: number | string | null;
  rate?: number | null;
  quantity?: number | null;
  item_total?: number | null;
}): boolean {
  const taxAmount = getLineItemTaxAmount(item);
  const raw = String(item.tax_name ?? "").trim();
  if (taxAmount !== 0) return true;
  if (raw.length > 0 && raw !== "empty") return true;
  return false;
}

/**
 * Left label for the tax row: prefer Zoho tax name exactly, without adding a percentage suffix.
 */
export function getLineItemTaxRowLabel(item: {
  tax_name?: string | null;
  tax_amount?: number | null;
  tax_percentage?: number | string | null;
  tax_id?: string | null;
  rate?: number | null;
  quantity?: number | null;
  item_total?: number | null;
}): string {
  const taxAmount = getLineItemTaxAmount(item);
  const raw = String(item.tax_name ?? "").trim();

  if (raw && raw !== "empty") {
    return raw.toUpperCase();
  }
  if (taxAmount > 0) {
    return "Tax";
  }
  return "No Tax";
}
